import {isDynamoWrapper} from '@packages/utilities/elementTypeUtils';
import {isConnectedDynamoWrapper} from '@packages/systems/dynamo/utils/DynamoElementUtils';

import {analyzeBoundaryCreators} from './analyzers';
import {
  getBoundaryCreatorError,
  getMismatchedBindingContextsError,
  getMismatchedParentBindingContextsError,
} from './errors';

import {type ExpressionElement} from '@packages/systems/wfdl-ast';
import type {InternalBindingContextGraphNode} from '../BindingContextGraph';
import type {ConsolidatedBindingContext} from '../consolidateBindingContext';
import type {
  DynamicContext,
  DynamicContentFlags,
  TopLevelDynamicContentFlags,
} from './types';

type ValidatorFunctionArgs = {
  topLevelDynamicContentFlags: TopLevelDynamicContentFlags;
  topLevelBoundaryCreators: ExpressionElement[];
  targetNode: InternalBindingContextGraphNode;
  sourceNode: InternalBindingContextGraphNode;
  target: DynamicContext;
};

type ErrorMessageOrVoid = string | undefined;

const VALIDATOR_FUNCTIONS = [
  validateBoundaryCreators,
  validateBindingContextsMatch,
  validateParentBindingContextsMatch,
];

export function getValidationErrorMessage(
  validatorArgs: ValidatorFunctionArgs
): ErrorMessageOrVoid {
  for (const validator of VALIDATOR_FUNCTIONS) {
    const errorMessage = validator(validatorArgs);
    if (errorMessage !== undefined) {
      return errorMessage;
    }
  }
}

function validateBoundaryCreators({
  topLevelBoundaryCreators,
  targetNode,
}: ValidatorFunctionArgs): ErrorMessageOrVoid {
  const incompatibleBoundaryCreator = findIncompatibleBoundaryCreator(
    targetNode,
    topLevelBoundaryCreators
  );

  if (incompatibleBoundaryCreator) {
    return getBoundaryCreatorError(incompatibleBoundaryCreator);
  }
}

function validateBindingContextsMatch({
  topLevelDynamicContentFlags,
  topLevelBoundaryCreators,
  targetNode,
  sourceNode,
  target,
}: ValidatorFunctionArgs): ErrorMessageOrVoid {
  const dynamicContentFlags = {
    ...topLevelDynamicContentFlags,
    ...analyzeBoundaryCreators(topLevelBoundaryCreators),
  } as const;

  if (shouldValidateBindingContextMatch(dynamicContentFlags)) {
    const targetBindingContext = targetNode.getBindingContext();
    const sourceBindingContext = sourceNode.getBindingContext();

    if (!bindingContextsMatch(targetBindingContext, sourceBindingContext)) {
      return getMismatchedBindingContextsError(
        dynamicContentFlags,
        targetBindingContext,
        target
      );
    }
  }
}

function validateParentBindingContextsMatch({
  topLevelDynamicContentFlags,
  targetNode,
  sourceNode,
}: ValidatorFunctionArgs): ErrorMessageOrVoid {
  if (topLevelDynamicContentFlags.orphanContextualConditions) {
    const targetParentBindingContext = targetNode.getParentBindingContext();
    const sourceParentBindingContext = sourceNode.getParentBindingContext();

    if (
      !bindingContextsMatch(
        targetParentBindingContext,
        sourceParentBindingContext
      )
    ) {
      return getMismatchedParentBindingContextsError();
    }
  }
}

function findIncompatibleBoundaryCreator(
  bindingContextNode: InternalBindingContextGraphNode,
  boundaryCreators: ExpressionElement[]
): ExpressionElement | null | undefined {
  return boundaryCreators.find(
    isIncompatibleBoundaryCreator(bindingContextNode)
  );
}

const isIncompatibleBoundaryCreator =
  (bindingContextNode: InternalBindingContextGraphNode) =>
  (element: ExpressionElement) => {
    const boundary = bindingContextNode.getBindingContextBoundary(element);

    if (boundary === null || boundary.nextBindingContext !== null) {
      return false;
    }

    // HACK: The binding context boundary of an unconnected collection list
    // looks identical to an incompatible binding context boundary. We work
    // around this issue by checking if the collection list is connected.
    return isDynamoWrapper(element) ? isConnectedDynamoWrapper(element) : true;
  };

function shouldValidateBindingContextMatch(
  flags: DynamicContentFlags
): boolean {
  return (
    flags.orphanBindingsOrConditions ||
    flags.collectionListsWithContextualFilters ||
    flags.collectionListsWithContextualConditions ||
    flags.orphanContextualFilterProps
  );
}

function bindingContextsMatch(
  context: ConsolidatedBindingContext | null,
  anotherContext: ConsolidatedBindingContext | null
): boolean {
  return Boolean(context && anotherContext && context.matches(anotherContext));
}
